import React, {createContext, useContext, useMemo} from 'react'
import PropTypes from 'prop-types'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getLocaleByReference} from '@salesforce/retail-react-app/app/utils/site-utils'

const NavigationContext = createContext(null)

export const NavigationProvider = ({children}) => {
    const {site, locale, buildUrl} = useMultiSite()

    const localeRef =
        getLocaleByReference(site, locale?.id || locale?.alias)?.alias ||
        locale?.alias ||
        locale?.id

    const value = useMemo(() => {
        const normalizePathname = (pathname = '') => {
            const pathOnly = String(pathname).split(/[?#]/)[0]
            return (pathOnly.replace(/\/+$/, '') || '/').toLowerCase()
        }
        const normalizeLocale = (value = '') => String(value).replace('_', '-').toLowerCase()
        const localeRoots = new Set()
        const supportedLocales = site?.l10n?.supportedLocales || []

        supportedLocales.forEach((item) => {
            if (typeof item === 'string') {
                const normalized = normalizeLocale(item)
                if (normalized) localeRoots.add(`/${normalized}`)
                return
            }

            const id = normalizeLocale(item?.id)
            const alias = normalizeLocale(item?.alias)
            if (id) localeRoots.add(`/${id}`)
            if (alias) localeRoots.add(`/${alias}`)
        })

        const isHomepagePath = (pathname = '') => {
            const normalizedPath = normalizePathname(pathname)
            const localeHome = localeRef ? normalizePathname(`/${localeRef}`) : null
            const localizedHomePath = normalizePathname(buildUrl('/'))
            const isShortRoot = /^\/[a-z]{2}(?:-[a-z]{2})?$/.test(normalizedPath)
            return (
                normalizedPath === '/' ||
                normalizedPath === localeHome ||
                normalizedPath === localizedHomePath ||
                localeRoots.has(normalizedPath) ||
                isShortRoot
            )
        }

        return {isHomepagePath}
    }, [localeRef, buildUrl, site?.l10n?.supportedLocales])

    return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>
}

NavigationProvider.propTypes = {
    children: PropTypes.node.isRequired
}

export const useNavigationContext = () => {
    const context = useContext(NavigationContext)
    if (!context) {
        throw new Error('useNavigationContext must be used within NavigationProvider')
    }
    return context
}

export default NavigationContext
